import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Phone, X, ChevronDown, MessageSquare, UserPlus, AlertCircle, RefreshCw, Briefcase, Users as UsersIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useVoice } from '@/context/VoiceContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function getInitials(first, last) {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase() || '?';
}

const COLORS = [
  'bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
];
function avatarColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return COLORS[h % COLORS.length];
}

function normalizePhone(raw) {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return raw.startsWith('+') ? raw : `+${digits}`;
}

const PAGE_SIZE = 50;
const FETCH_TIMEOUT_MS = 8000;

function ContactRow({ contact, mode, onCall, onText, subtitle, isTeam }) {
  const navigate = useNavigate();
  const [showNumbers, setShowNumbers] = useState(false);
  const hasMultiple = !isTeam && (contact.additional_phones || []).length > 0;
  // Fix S (2026-09-14): additional_phones puede traer {name,phone,label} ademas de string.
  const additionalPhoneStrs = (isTeam ? [] : (contact.additional_phones || [])).map(p => (typeof p === 'string' ? p : (p?.phone || ''))).filter(Boolean);
  const allPhones = [contact.phone, ...additionalPhoneStrs].filter(Boolean);
  const name = isTeam ? contact.name : `${contact.first_name || ''} ${contact.last_name || ''}`.trim();

  const handleAction = (phone) => {
    if (mode === 'text') {
      onText && onText({ number: normalizePhone(phone), contact_id: isTeam ? undefined : contact.id, contact_name: name });
    } else {
      onCall(normalizePhone(phone), name);
    }
  };

  const handlePrimaryClick = (e) => {
    e.stopPropagation();
    if (hasMultiple) {
      setShowNumbers(s => !s);
    } else if (contact.phone) {
      handleAction(contact.phone);
    }
  };

  return (
    <div className="relative">
      <div
        className={`flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 group transition-colors ${isTeam ? '' : 'cursor-pointer'}`}
        onClick={() => !isTeam && navigate(`/contacts/${contact.id}`)}
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${avatarColor(name)}`}>
          <span className="text-white text-xs font-semibold">{getInitials(contact.first_name, contact.last_name || (isTeam ? name.split(' ')[1] : ''))}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
          {subtitle && <p className="text-xs text-slate-400 truncate capitalize">{subtitle}</p>}
          {!subtitle && contact.company_name && (
            <p className="text-xs text-slate-400 truncate">{contact.company_name}</p>
          )}
          {!subtitle && contact.phone && !contact.company_name && (
            <p className="text-xs text-slate-400 truncate font-mono">{contact.phone}</p>
          )}
        </div>
        {contact.phone && (
          <button
            data-testid={`contact-${mode === 'text' ? 'text' : 'call'}-${isTeam ? 'team-' : ''}${contact.id}`}
            onClick={handlePrimaryClick}
            className={`
              opacity-0 group-hover:opacity-100
              w-8 h-8 rounded-full
              ${mode === 'text' ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}
              flex items-center justify-center flex-shrink-0 transition-all
            `}
          >
            {hasMultiple
              ? <ChevronDown className="w-4 h-4" />
              : (mode === 'text' ? <MessageSquare className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />)}
          </button>
        )}
      </div>

      {/* Multi-phone dropdown */}
      {showNumbers && (
        <div className="absolute right-4 top-full z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[180px]">
          {allPhones.map((ph, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                handleAction(ph);
                setShowNumbers(false);
              }}
              className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 w-full text-left"
            >
              {mode === 'text'
                ? <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                : <Phone className="w-3.5 h-3.5 text-slate-400" />}
              <span className="text-sm font-mono text-slate-700">{ph}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const emptyForm = { first_name: '', last_name: '', phone: '', email: '', account_type: 'residential', company_name: '' };

export default function ContactsPanel({ onDialTo, mode = 'call', onText, refreshTrigger }) {
  const { makeCall, device, callStatus, callerInfo } = useVoice();
  const [contacts, setContacts] = useState([]);
  const [team, setTeam] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const listRef = useRef(null);

  // New Customer modal
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/voice/team-directory').then(r => setTeam(r.data || [])).catch(() => setTeam([]));
  }, []);

  const fetchContacts = useCallback(async (q = '', pageNum = 1, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setLoadError(false);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const params = new URLSearchParams({ page: pageNum, page_size: PAGE_SIZE });
      if (q) params.set('search', q);
      const res = await api.get(`/contacts?${params.toString()}`, { signal: controller.signal });
      const items = res.data.items || res.data.contacts || res.data || [];
      setContacts(prev => append ? [...prev, ...items] : items);
      setHasMore(items.length >= PAGE_SIZE);
      setPage(pageNum);
    } catch (err) {
      if (!append) { setContacts([]); setLoadError(true); }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts(search, 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when a contact is created/updated inline from the message thread.
  useEffect(() => {
    if (refreshTrigger) fetchContacts(search, 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchContacts(val, 1, false), 300);
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      fetchContacts(search, page + 1, true);
    }
  };

  const handleCall = async (number, name) => {
    if (!device) {
      toast.error('Voice service not available');
      return;
    }
    if (callStatus !== 'idle') {
      toast.warning('Already on a call');
      return;
    }
    try {
      await makeCall(number, name);
    } catch (err) {
      toast.error(`Call failed: ${err.message}`);
    }
  };

  const openNewCustomer = () => {
    setForm({ ...emptyForm, phone: callStatus !== 'idle' ? (callerInfo?.number || '') : '' });
    setShowNewCustomer(true);
  };

  const handleCreateCustomer = async () => {
    if (!form.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        account_type: form.account_type,
        company_name: form.account_type === 'commercial' ? form.company_name.trim() : '',
        type: 'customer',
        is_customer: true,
      };
      const res = await api.post('/contacts', payload);
      setContacts(prev => [res.data, ...prev]);
      toast.success('Customer created');
      setShowNewCustomer(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create customer');
    } finally {
      setSaving(false);
    }
  };

  const teamFiltered = search
    ? team.filter(u => `${u.name} ${u.phone} ${u.role}`.toLowerCase().includes(search.toLowerCase()))
    : team;

  return (
    <div className="flex flex-col h-full">
      {/* Search + New Customer */}
      <div className="p-3 border-b border-slate-100 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={searchRef}
            data-testid="contacts-search-input"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search contacts..."
            className="
              w-full pl-9 pr-8 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl
              focus:outline-none focus:border-[#003366]/30 focus:ring-2 focus:ring-[#003366]/8
              text-slate-700 placeholder-slate-400 transition-all
            "
          />
          {search && (
            <button
              onClick={() => handleSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          data-testid="new-customer-btn"
          onClick={openNewCustomer}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#003366]/5 text-[#003366] text-xs font-semibold hover:bg-[#003366]/10 transition-colors"
        >
          <UserPlus className="w-3.5 h-3.5" /> New Customer
        </button>
      </div>

      {/* List */}
      <div ref={listRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin mr-2" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2" data-testid="contacts-load-error">
            <AlertCircle className="w-8 h-8 opacity-30 text-rose-400" />
            <p className="text-sm text-rose-500">Could not load contacts</p>
            <button
              data-testid="contacts-retry-btn"
              onClick={() => fetchContacts(search, 1, false)}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#003366] hover:underline"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        ) : (
          <>
            {teamFiltered.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 flex items-center gap-1.5">
                  <Briefcase className="w-3 h-3 text-slate-400" />
                  <span className="text-[10px] font-bold text-slate-400 tracking-wider">TEAM</span>
                </div>
                {teamFiltered.map(u => (
                  <ContactRow key={`team-${u.id}`} contact={u} mode={mode} onCall={handleCall} onText={onText} subtitle={u.role} isTeam />
                ))}
              </div>
            )}
            {(teamFiltered.length > 0) && (
              <div className="px-4 pt-3 pb-1 flex items-center gap-1.5">
                <UsersIcon className="w-3 h-3 text-slate-400" />
                <span className="text-[10px] font-bold text-slate-400 tracking-wider">CUSTOMERS</span>
              </div>
            )}
            {contacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Search className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-sm">{search ? 'No results' : 'No contacts'}</p>
              </div>
            ) : (
              contacts.map(c => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  mode={mode}
                  onCall={handleCall}
                  onText={onText}
                  onDialTo={onDialTo}
                />
              ))
            )}
            {loadingMore && (
              <div className="flex items-center justify-center py-3 text-slate-400">
                <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
              </div>
            )}
          </>
        )}
      </div>

      <div className="px-4 py-2 border-t border-slate-100">
        <p className="text-[10px] text-slate-400">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}{team.length > 0 ? ` · ${team.length} team` : ''}</p>
      </div>

      {/* New Customer modal — never blocks/closes an active call */}
      <Dialog open={showNewCustomer} onOpenChange={setShowNewCustomer}>
        <DialogContent className="max-w-sm" data-testid="new-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#003366]" /> New Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">First name *</Label>
                <Input data-testid="new-customer-first-name" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Last name</Label>
                <Input data-testid="new-customer-last-name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Phone</Label>
              <Input data-testid="new-customer-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 (555) 123-4567" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="new-customer-type-residential"
                  onClick={() => setForm({ ...form, account_type: 'residential' })}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.account_type === 'residential' ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Residential
                </button>
                <button
                  type="button"
                  data-testid="new-customer-type-commercial"
                  onClick={() => setForm({ ...form, account_type: 'commercial' })}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.account_type === 'commercial' ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Commercial
                </button>
              </div>
            </div>
            {form.account_type === 'commercial' && (
              <div className="space-y-1">
                <Label className="text-xs">Company name</Label>
                <Input data-testid="new-customer-company" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Email (optional)</Label>
              <Input data-testid="new-customer-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setShowNewCustomer(false)} disabled={saving}>Cancel</Button>
            <Button type="button" data-testid="new-customer-save-btn" onClick={handleCreateCustomer} disabled={saving} className="bg-[#003366] hover:bg-[#004080] text-white">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
